import type { PointTerritorialContextApi } from "@/lib/api";
import { lotReferenceView, lotRelationLabel } from "@/config/lot-relation-config";
import { formatUtm } from "@/lib/utm";

interface DerivedContextPanelProps {
  context: PointTerritorialContextApi | null;
  loading: boolean;
}

/**
 * What the geometry already answers about the chosen point.
 *
 * Shown before saving so the operator can see the derivation instead of
 * discovering it afterwards in the record — and can correct it in the next
 * step, since a typed value always wins over a derived one.
 */
export default function DerivedContextPanel({ context, loading }: DerivedContextPanelProps) {
  if (loading) return null;

  const rows: Array<[string, string]> = [];
  if (context?.neighborhood) rows.push(["Bairro", context.neighborhood]);
  if (context?.region) rows.push(["Região", context.region.charAt(0) + context.region.slice(1).toLowerCase()]);
  if (context?.street) {
    const distance =
      context.street_distance_m != null
        ? ` (a ${context.street_distance_m.toFixed(1).replace(".", ",")} m)`
        : "";
    rows.push(["Logradouro mais próximo", `${context.street}${distance}`]);
  }
  if (context?.quadra_code) rows.push(["Quadra", context.quadra_code]);
  if (context?.cadastral_block_code) rows.push(["Quadra cadastral", context.cadastral_block_code]);
  const lot = context?.lot_relation_suggestion;
  const ref = lot?.reference ? lotReferenceView(lot.reference) : null;
  if (ref?.title) {
    rows.push([ref.title, `Lote ${ref.lot}, quadra ${ref.block}`]);
    if (ref.adjacent) rows.push(["Lote vizinho (divisa)", ref.adjacent]);
  } else if (context?.lot_number) {
    rows.push(["Lote", context.lot_number]);
  }
  if (ref && ref.cep !== "—") rows.push(["CEP", ref.cep]);
  else if (ref?.cepNote && ref.cep === "—" && lot?.reference?.cep_status === "AMBIGUO") rows.push(["CEP", ref.cepNote]);
  if (lot) {
    const label =
      lot.code === "INDETERMINADO"
        ? `Indeterminado — ${lot.candidates.map(lotRelationLabel).join(" ou ")}`
        : lotRelationLabel(lot.code);
    rows.push(["Relação com o lote (sugestão)", label]);
  }
  if (context?.municipality_name) rows.push(["Município", context.municipality_name]);
  if (context?.utm_e != null && context?.utm_n != null) {
    rows.push(["UTM 22S (EPSG:31982)", `E ${formatUtm(context.utm_e)} · N ${formatUtm(context.utm_n)}`]);
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-background-50 border border-background-200 px-3 py-2.5">
        <i className="ri-map-pin-line text-foreground-400 mt-0.5" />
        <p className="text-xs text-foreground-500">
          Esta posição está fora da área com base cadastral carregada, então bairro,
          quadra e logradouro não puderam ser derivados — será preciso informá-los na
          próxima etapa.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-background-200 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-background-50 border-b border-background-200">
        <i className="ri-sparkling-line text-primary-600" />
        <p className="text-xs font-medium text-foreground-700">
          Derivado da posição — não precisa digitar
        </p>
      </div>
      <dl className="divide-y divide-background-200">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-start gap-3 px-3 py-2">
            <dt className="text-xs text-foreground-500 w-44 shrink-0">{label}</dt>
            <dd className="text-sm text-foreground-900">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
